import React, { useEffect, useState, useMemo } from "react";
import api, { otherExpensesApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { exportToCsv, timestampedName } from "../lib/exportToCsv";
import ExportCurrencyButton, { buildExportPreamble } from "../components/ExportCurrencyButton";
import {
  DollarSign,
  Calendar,
  CheckCircle2,
  Clock,
  Plus,
  Upload,
  ChevronLeft,
  ChevronRight,
  Building2,
  CreditCard,
  Phone,
  FileSpreadsheet,
  Landmark,
  Trash2,
  Filter,
  X,
  ThumbsUp,
  XCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  User,
  Truck,
  Megaphone,
  Paperclip,
} from "lucide-react";

const EXPENSE_TYPES = {
  // Supplier and advertising were the gap: external services had to be filed
  // as "Other", so a report could not tell them apart. backlog b6b42a2b.
  supplier: { label: "Supplier", icon: Truck, color: "text-amber-400" },
  advertising: { label: "Advertising", icon: Megaphone, color: "text-pink-400" },
  office: { label: "Office", icon: Building2, color: "text-sky-400" },
  subscription: { label: "Subscription", icon: CreditCard, color: "text-orange-400" },
  phone: { label: "Phone", icon: Phone, color: "text-green-400" },
  software: { label: "Software", icon: FileSpreadsheet, color: "text-purple-400" },
  taxes: { label: "Taxes", icon: Landmark, color: "text-rose-400" },
  other: { label: "Other", icon: DollarSign, color: "text-zinc-400" },
};

const OtherExpensesPage = () => {
  const { formatAmount, rates, rateDate, convertAmount, convertAmountExplicit } = useCurrency();
  const [expenses, setExpenses] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Filters
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAuthor, setFilterAuthor] = useState("all");
  // Sort: field = "date" | "amount" | null
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  
  // New expense form
  const [newExpense, setNewExpense] = useState({
    payee_name: "",
    amount: "",
    currency: "UAH",
    expense_type: "other",
    service_kind: "",
    project_name: "",
    payment_details: "",
    notes: "",
    date: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    try {
      const [expensesRes, statsRes] = await Promise.all([
        otherExpensesApi.getAll({ month: selectedMonth }),
        otherExpensesApi.getStats("other"),  // Only non-artist expenses
      ]);
      setExpenses(expensesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await otherExpensesApi.importExcel();
      toast.success(`Imported ${result.data.total_imported} records: ${result.data.artist_payments} artist payments, ${result.data.other_expenses} other expenses`);
      setImportDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import data");
    } finally {
      setImporting(false);
    }
  };

  const handleAttach = async (expenseId, file) => {
    if (!file) return;
    setUploadingFor(expenseId);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/other-expenses/${expenseId}/attachment`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document attached");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to attach document");
    } finally {
      setUploadingFor(null);
    }
  };

  const handleAddExpense = async () => {
    if (!newExpense.payee_name || !newExpense.amount) {
      toast.error("Please fill required fields");
      return;
    }
    try {
      await otherExpensesApi.create({
        ...newExpense,
        amount: parseFloat(newExpense.amount),
      });
      toast.success("Expense added");
      setAddDialogOpen(false);
      setNewExpense({
        payee_name: "",
        amount: "",
        currency: "UAH",
        expense_type: "other",
        project_name: "",
        payment_details: "",
        notes: "",
        date: new Date().toISOString().slice(0, 10),
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add expense");
    }
  };

  const handleApprove = async (expenseId) => {
    try {
      await otherExpensesApi.update(expenseId, { status: "approved" });
      toast.success("Approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve");
    }
  };

  const handleCancel = async (expenseId) => {
    try {
      await otherExpensesApi.update(expenseId, { status: "cancelled" });
      toast.success("Cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  const handleMarkPaid = async (expenseId) => {
    try {
      await otherExpensesApi.update(expenseId, { status: "paid" });
      toast.success("Marked as paid");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (expenseId) => {
    try {
      await otherExpensesApi.delete(expenseId);
      toast.success("Deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${monthNames[month - 1]} ${year}`;
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
  };

  // Author options (from submitted_by_name) — distinct, sorted
  const authorOptions = useMemo(() => {
    const set = new Set();
    expenses.forEach((exp) => {
      if (exp.expense_type === "artist_payment") return;
      const name = (exp.submitted_by_name || "").trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [expenses]);

  // Filter expenses - exclude artist_payment and cancelled (shown on Artist Payments page)
  const filteredExpenses = useMemo(() => {
    const list = expenses.filter(exp => {
      // Don't show artist payments here - they go to Artist Payments page
      if (exp.expense_type === "artist_payment") return false;
      // Don't show cancelled by default unless specifically filtered
      if (filterStatus === "all" && exp.status === "cancelled") return false;
      if (filterType !== "all" && exp.expense_type !== filterType) return false;
      if (filterStatus !== "all" && exp.status !== filterStatus) return false;
      if (filterAuthor === "__unset__") {
        if ((exp.submitted_by_name || "").trim()) return false;
      } else if (filterAuthor !== "all") {
        if ((exp.submitted_by_name || "").trim() !== filterAuthor) return false;
      }
      return true;
    });
    if (!sortField) return list;
    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortField === "amount") {
        return ((a.amount ?? 0) - (b.amount ?? 0)) * dir;
      }
      // date sort — null/empty go last regardless of direction
      const av = a.date ? new Date(a.date).getTime() : Number.NaN;
      const bv = b.date ? new Date(b.date).getTime() : Number.NaN;
      const aMissing = Number.isNaN(av);
      const bMissing = Number.isNaN(bv);
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return (av - bv) * dir;
    });
    return sorted;
  }, [expenses, filterType, filterStatus, filterAuthor, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir(field === "date" ? "desc" : "desc");
      return;
    }
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  const sortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  // Calculate stats for current month/filters (exclude cancelled).
  // Items can be in mixed currencies (UAH/USD/EUR), so normalize each to UAH
  // before summing. Cards then call formatAmount(value, "UAH") which converts
  // the UAH-base sum to whatever displayCurrency the user picked.
  const monthStats = useMemo(() => {
    const toUAH = (amount, currency) => {
      const a = Number(amount);
      if (!a || isNaN(a)) return 0;
      const from = (currency || "UAH").toUpperCase();
      if (from === "UAH") return a;
      return a * (rates?.[from] || 1);
    };
    return filteredExpenses.filter(e => e.status !== "cancelled").reduce(
      (acc, exp) => {
        const inUAH = toUAH(exp.amount, exp.currency);
        acc.total += inUAH;
        if (exp.status === "paid") {
          acc.paid += inUAH;
        } else if (exp.status === "approved") {
          acc.approved += inUAH;
        } else {
          acc.pending += inUAH;
        }
        return acc;
      },
      { total: 0, paid: 0, pending: 0, approved: 0 }
    );
  }, [filteredExpenses, rates]);

  const clearFilters = () => {
    setFilterType("all");
    setFilterStatus("all");
    setFilterAuthor("all");
  };

  const hasActiveFilters = filterType !== "all" || filterStatus !== "all" || filterAuthor !== "all";

  const handleExport = (targetCurrency = "ORIGINAL") => {
    if (filteredExpenses.length === 0) {
      toast.error("Nothing to export with current filters");
      return;
    }
    const convert = targetCurrency !== "ORIGINAL";
    const rows = filteredExpenses.map((e) => {
      const native = Number(e.amount) || 0;
      const nativeCur = (e.currency || "UAH").toUpperCase();
      const exportedAmount = convert
        ? Math.round(convertAmountExplicit(native, nativeCur, targetCurrency) * 100) / 100
        : native;
      return {
        date: e.date || "",
        type: EXPENSE_TYPES[e.expense_type]?.label || e.expense_type || "",
        payee: e.payee_name || "",
        submitted_by: e.submitted_by_name || "",
        project: e.project_name || "",
        amount: exportedAmount,
        currency: convert ? targetCurrency : nativeCur,
        ...(convert ? { original_amount: native, original_currency: nativeCur } : {}),
        status: e.status || "",
        paid_date: e.paid_date || "",
        payment_details: e.payment_details || "",
        notes: e.notes || e.description || "",
      };
    });
    const columns = [
      { key: "date", header: "Date" },
      { key: "type", header: "Type" },
      { key: "payee", header: "Payee" },
      { key: "submitted_by", header: "Submitted By" },
      { key: "project", header: "Project" },
      { key: "amount", header: convert ? `Amount (${targetCurrency})` : "Amount" },
      { key: "currency", header: "Currency" },
      ...(convert ? [
        { key: "original_amount", header: "Original Amount" },
        { key: "original_currency", header: "Original Currency" },
      ] : []),
      { key: "status", header: "Status" },
      { key: "paid_date", header: "Paid Date" },
      { key: "payment_details", header: "Payment Details" },
      { key: "notes", header: "Notes" },
    ];
    const preamble = buildExportPreamble(targetCurrency, rates, rateDate);
    exportToCsv(
      timestampedName(`other-expenses-${selectedMonth}${convert ? `-${targetCurrency}` : ""}`),
      rows,
      columns,
      preamble ? { preamble } : undefined,
    );
    toast.success(
      convert
        ? `Exported ${rows.length} rows · all amounts in ${targetCurrency}`
        : `Exported ${rows.length} rows`
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="other-expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Other Expenses</h1>
          <p className="text-sm text-zinc-500">Office, subscriptions & other company costs</p>
        </div>

        <div className="flex gap-2">
          <ExportCurrencyButton onExport={handleExport} />
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300" data-testid="import-btn">
                <Upload className="h-4 w-4 mr-2" />
                Import Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50 flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-green-400" />
                  Import from Excel
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-zinc-400 text-sm">
                  This will import historical expenses from the uploaded Excel file (smarto-applicaitons-2026.xlsx).
                </p>
                <p className="text-zinc-400 text-sm">
                  Records will be categorized as:
                </p>
                <ul className="text-zinc-400 text-sm list-disc pl-5 space-y-1">
                  <li><span className="text-purple-400">Artist Payments</span> - payments to external artists</li>
                  <li><span className="text-sky-400">Office/Other</span> - company expenses (Figma, phone, etc.)</li>
                </ul>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="border-zinc-700">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleImport} 
                    disabled={importing}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-import-btn"
                  >
                    {importing ? "Importing..." : "Import Data"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-expense-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50">Add New Expense</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label className="text-zinc-300">Payee Name *</Label>
                    <Input
                      value={newExpense.payee_name}
                      onChange={(e) => setNewExpense({...newExpense, payee_name: e.target.value})}
                      placeholder="Enter name..."
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                      data-testid="payee-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Amount *</Label>
                    <Input
                      type="number"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                      placeholder="0"
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                      data-testid="amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Currency</Label>
                    <Select value={newExpense.currency} onValueChange={(v) => setNewExpense({...newExpense, currency: v})}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                        <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                        <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Type</Label>
                    <Select value={newExpense.expense_type} onValueChange={(v) => setNewExpense({...newExpense, expense_type: v})}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {Object.entries(EXPENSE_TYPES).map(([key, { label }]) => (
                          <SelectItem key={key} value={key} className="text-zinc-50">{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Date</Label>
                    <Input
                      type="date"
                      value={newExpense.date}
                      onChange={(e) => setNewExpense({...newExpense, date: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    />
                  </div>
                  {["supplier", "advertising"].includes(newExpense.expense_type) && (
                    <div className="col-span-2 space-y-2">
                      <Label className="text-zinc-300">Service</Label>
                      <Input
                        value={newExpense.service_kind}
                        onChange={(e) => setNewExpense({...newExpense, service_kind: e.target.value})}
                        placeholder="What was bought — hosting, print, media buying..."
                        className="bg-zinc-950 border-zinc-800 text-zinc-50"
                        data-testid="service-kind-input"
                      />
                    </div>
                  )}
                  <div className="col-span-2 space-y-2">
                    <Label className="text-zinc-300">Project Name</Label>
                    <Input
                      value={newExpense.project_name}
                      onChange={(e) => setNewExpense({...newExpense, project_name: e.target.value})}
                      placeholder="Optional..."
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-zinc-300">Payment Details (Card/IBAN)</Label>
                    <Input
                      value={newExpense.payment_details}
                      onChange={(e) => setNewExpense({...newExpense, payment_details: e.target.value})}
                      placeholder="Optional..."
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-zinc-300">Notes</Label>
                    <Input
                      value={newExpense.notes}
                      onChange={(e) => setNewExpense({...newExpense, notes: e.target.value})}
                      placeholder="Optional..."
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-zinc-700">
                    Cancel
                  </Button>
                  <Button onClick={handleAddExpense} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-add-btn">
                    Add Expense
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Total</span>
              <DollarSign className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-400">{formatAmount(monthStats.total, "UAH")}</p>
            <p className="text-xs text-zinc-500 mt-1">{filteredExpenses.length} expenses</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Paid</span>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-400">{formatAmount(monthStats.paid, "UAH")}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Pending</span>
              <Clock className="h-4 w-4 text-sky-500" />
            </div>
            <p className="text-2xl font-bold text-sky-400">{formatAmount(monthStats.pending + monthStats.approved, "UAH")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Month Navigation & Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => navigateMonth(-1)}
            className="text-zinc-400 hover:text-zinc-50"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <h2 className="text-lg font-semibold text-zinc-50 font-['Manrope'] min-w-[150px] text-center">
            {formatMonth(selectedMonth)}
          </h2>
          <Button
            variant="ghost"
            onClick={() => navigateMonth(1)}
            className="text-zinc-400 hover:text-zinc-50"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-zinc-500" />
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-zinc-50">All Types</SelectItem>
                {Object.entries(EXPENSE_TYPES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key} className="text-zinc-50">{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" className="text-zinc-50">All Status</SelectItem>
              <SelectItem value="pending" className="text-zinc-50">Pending</SelectItem>
              <SelectItem value="approved" className="text-zinc-50">Approved</SelectItem>
              <SelectItem value="paid" className="text-zinc-50">Paid</SelectItem>
              <SelectItem value="cancelled" className="text-zinc-50">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-zinc-500" />
            <Select value={filterAuthor} onValueChange={setFilterAuthor}>
              <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9" data-testid="filter-author">
                <SelectValue placeholder="Author" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[320px]">
                <SelectItem value="all" className="text-zinc-50">All Authors</SelectItem>
                <SelectItem value="__unset__" className="text-zinc-50">— No author —</SelectItem>
                {authorOptions.map((name) => (
                  <SelectItem key={name} value={name} className="text-zinc-50">{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-zinc-400 hover:text-zinc-50 h-9"
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Expenses Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No expenses found</p>
              <p className="text-xs text-zinc-600 mt-1">Add expenses or import from Excel</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 font-medium">
                    <button
                      type="button"
                      onClick={() => toggleSort("date")}
                      className="inline-flex items-center gap-1 hover:text-zinc-50 transition-colors"
                      data-testid="sort-date"
                    >
                      Date
                      {sortIcon("date")}
                    </button>
                  </TableHead>
                  <TableHead className="text-zinc-400 font-medium">Type</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Payee</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Project</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">
                    <button
                      type="button"
                      onClick={() => toggleSort("amount")}
                      className="inline-flex items-center gap-1 hover:text-zinc-50 transition-colors ml-auto"
                      data-testid="sort-amount"
                    >
                      Amount
                      {sortIcon("amount")}
                    </button>
                  </TableHead>
                  <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => {
                  const TypeIcon = EXPENSE_TYPES[expense.expense_type]?.icon || DollarSign;
                  const typeColor = EXPENSE_TYPES[expense.expense_type]?.color || "text-zinc-400";
                  
                  return (
                    <TableRow 
                      key={expense.id} 
                      className="border-zinc-800 hover:bg-zinc-800/50"
                      data-testid={`expense-row-${expense.id}`}
                    >
                      <TableCell>
                        <span className="text-sm text-zinc-300 font-mono">
                          {expense.date ? new Date(expense.date).toLocaleDateString() : "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TypeIcon className={cn("h-4 w-4", typeColor)} />
                          <span className={cn("text-sm", typeColor)}>
                            {EXPENSE_TYPES[expense.expense_type]?.label || expense.expense_type}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-zinc-50 font-medium text-sm">{expense.payee_name}</p>
                          {expense.submitted_by_name && (
                            <p className="text-xs text-purple-400">📤 Submitted by: {expense.submitted_by_name}</p>
                          )}
                          {expense.payee_name_en && expense.payee_name_en !== expense.payee_name && (
                            <p className="text-xs text-green-400">✓ {expense.payee_name_en}</p>
                          )}
                          {expense.expense_type === "artist_payment" && !expense.artist_id && (
                            <p className="text-xs text-orange-400">⚠ Not linked</p>
                          )}
                          {expense.service_kind && (
                            <p className="text-xs text-amber-400/90">{expense.service_kind}</p>
                          )}
                          {(expense.description || expense.notes) && (
                            <p className="text-xs text-zinc-500 whitespace-pre-wrap break-words">{expense.description || expense.notes}</p>
                          )}
                          {/* The invoice lives with the payment, not in someone's mail. */}
                          <div className="mt-1">
                            {expense.attachment_url ? (
                              <a href={expense.attachment_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300"
                                onClick={(e) => e.stopPropagation()}>
                                <Paperclip className="h-3 w-3" />
                                {expense.attachment_name || "Document"}
                              </a>
                            ) : (
                              <label className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-300 cursor-pointer">
                                <Paperclip className="h-3 w-3" />
                                {uploadingFor === expense.id ? "Uploading…" : "Attach invoice"}
                                <input type="file" className="hidden"
                                  accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.xlsx,.docx"
                                  onChange={(e) => handleAttach(expense.id, e.target.files?.[0])} />
                              </label>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-zinc-400">{expense.project_name || "—"}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-zinc-50">{formatAmount(expense.amount, expense.currency)}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        {expense.status === "paid" ? (
                          <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20" title={expense.paid_date ? `Paid on ${new Date(expense.paid_date).toLocaleDateString()}` : ""}>
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Paid
                            {expense.paid_date && (
                              <span className="ml-1 text-[10px] text-green-300/80 font-mono">
                                {new Date(expense.paid_date).toLocaleDateString()}
                              </span>
                            )}
                          </Badge>
                        ) : expense.status === "approved" ? (
                          <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20">
                            <ThumbsUp className="h-3 w-3 mr-1" />
                            Approved
                          </Badge>
                        ) : expense.status === "cancelled" ? (
                          <Badge variant="outline" className="bg-zinc-700/50 text-zinc-500 border-zinc-600">
                            <XCircle className="h-3 w-3 mr-1" />
                            Cancelled
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          {expense.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleApprove(expense.id)}
                                className="text-sky-400 hover:text-sky-300 hover:bg-sky-500/10 h-7 px-2"
                                title="Approve"
                              >
                                <ThumbsUp className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCancel(expense.id)}
                                className="text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700 h-7 px-2"
                                title="Cancel"
                              >
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          {expense.status === "approved" && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMarkPaid(expense.id)}
                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10 h-7 px-2"
                                title="Mark Paid"
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCancel(expense.id)}
                                className="text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700 h-7 px-2"
                                title="Cancel"
                              >
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                                data-testid={`delete-${expense.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-zinc-50">Delete Expense</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">
                                  Are you sure you want to delete this expense? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDelete(expense.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Monthly Summary */}
      {stats?.monthly_breakdown?.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800 mt-6">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
              <Calendar className="h-4 w-4 text-orange-500" />
              Monthly History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 font-medium">Month</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Total</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Paid</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Pending</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.monthly_breakdown.slice(0, 12).map((row) => (
                  <TableRow 
                    key={row.month}
                    className={cn(
                      "border-zinc-800 hover:bg-zinc-800/50 cursor-pointer",
                      row.month === selectedMonth && "bg-zinc-800/30"
                    )}
                    onClick={() => setSelectedMonth(row.month)}
                  >
                    <TableCell>
                      <span className="text-zinc-50 font-medium text-sm">{formatMonth(row.month)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-zinc-50 font-medium">{formatAmount(row.total, "UAH")}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-green-400">{formatAmount(row.paid, "UAH")}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-orange-400">{formatAmount(row.pending + (row.approved || 0), "UAH")}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      {row.pending === 0 && (row.approved || 0) === 0 && row.paid > 0 ? (
                        <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
                          Complete
                        </Badge>
                      ) : (row.pending > 0 || (row.approved || 0) > 0) ? (
                        <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20">
                          Partial
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-zinc-800 text-zinc-500 border-zinc-700">
                          No Data
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* TOTAL Row */}
                {stats.totals && (
                  <TableRow className="border-zinc-800 bg-zinc-800/30 font-bold">
                    <TableCell>
                      <span className="text-zinc-50 font-bold">TOTAL</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-zinc-50 font-bold">{formatAmount(stats.totals.total, "UAH")}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-green-400 font-bold">{formatAmount(stats.totals.paid, "UAH")}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-orange-400 font-bold">{formatAmount(stats.totals.pending + (stats.totals.approved || 0), "UAH")}</span>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OtherExpensesPage;
